import { useMemo, useState } from "react";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUIContent, AXNUIContentConfirmButton, AXNUITheme, AXNUIThemeConfirmButton } from "@iddataweb/idw-common-ui/types";
import { ThemingActionButtonValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ThemingActionButtonDefaultSection } from "./theming-action-button-default-section/theming-action-button-default-section";
import { ThemingActionButtonAsyncSection } from "./theming-action-button-async-section/theming-action-button-async-section";
import { ThemingActionButtonStyleSection } from "./theming-action-button-style-section/theming-action-button-style-section";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingActionButtonTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingActionButtonValidationErrors;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    hasDisplayableDocumentCaptureAttributes: boolean;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingActionButtonTabContainer = (props: ThemingActionButtonTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        hasDisplayableDocumentCaptureAttributes,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIContentConfirmButtonFieldValue = (key: keyof AXNUIContentConfirmButton, value: string) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            confirmButton: {
                ...axnuiContent.confirmButton,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const updateAXNUIThemeConfirmButtonFieldValue = (key: keyof AXNUIThemeConfirmButton, value: string | boolean) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            confirmButton: {
                ...axnuiTheme.confirmButton,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.ACTION_BUTTON);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection showSeparator={true}>
                            <ThemingActionButtonDefaultSection
                                axnuiContent={axnuiContent}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUIContent={inheritedAXNUIContent}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateAXNUIContentConfirmButtonFieldValue={updateAXNUIContentConfirmButtonFieldValue}
                                updateAXNUIThemeConfirmButtonFieldValue={updateAXNUIThemeConfirmButtonFieldValue}
                            />
                        </ThemingFormSection>
                        {hasDisplayableDocumentCaptureAttributes && (
                            <ThemingFormSection showSeparator={true}>
                                <ThemingActionButtonAsyncSection
                                    axnuiContent={axnuiContent}
                                    inheritedAXNUIContent={inheritedAXNUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    validationErrors={validationErrors}
                                    updateAXNUIContentConfirmButtonFieldValue={updateAXNUIContentConfirmButtonFieldValue}
                                />
                            </ThemingFormSection>
                        )}
                        <ThemingFormSection>
                            <ThemingActionButtonStyleSection
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateAXNUIThemeConfirmButtonFieldValue={updateAXNUIThemeConfirmButtonFieldValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};